import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export function Toggle({
  checked,
  onCheckedChange,
  ariaLabel,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        "relative inline-flex h-5 w-8 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft disabled:cursor-not-allowed disabled:opacity-40",
        checked ? "bg-primary" : "bg-line-strong"
      )}
    >
      <span aria-hidden className={cn("grid size-4 place-items-center rounded-full bg-white transition-transform", checked ? "translate-x-[14px]" : "translate-x-0.5")} />
    </button>
  );
}

export function Checkbox({
  checked,
  onCheckedChange,
  ariaLabel,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  return (
    <CheckboxPrimitive.Root
      checked={checked}
      onCheckedChange={(v) => onCheckedChange(v === true)}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        "grid size-5 shrink-0 place-items-center rounded-[3px] border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft disabled:cursor-not-allowed disabled:opacity-40",
        checked ? "border-primary bg-primary" : "border-line-strong bg-surface"
      )}
    >
      <CheckboxPrimitive.Indicator>
        <Check aria-hidden className="size-3.5 text-white" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export function RadioGroup({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string; description?: string }[];
  ariaLabel: string;
}) {
  return (
    <fieldset role="radiogroup" aria-label={ariaLabel} className="flex flex-col gap-2">
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <label
            key={o.value}
            className={cn(
              "flex cursor-pointer items-start gap-2.5 rounded-md border p-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft",
              selected ? "border-primary bg-selected-soft" : "border-line"
            )}
          >
            <input type="radio" name={ariaLabel} value={o.value} checked={selected} onChange={() => onChange(o.value)} className="sr-only" />
            <span aria-hidden className={cn("mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border", selected ? "border-primary" : "border-line-strong")}>
              {selected && <span className="size-2 rounded-full bg-primary" />}
            </span>
            <span className="min-w-0">
              <span className="block font-semibold text-ink">{o.label}</span>
              {o.description && <span className="mt-0.5 block text-xs text-muted">{o.description}</span>}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement> & { mono?: boolean }>(
  ({ className, mono, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(
        "w-full rounded-sm border border-line-strong bg-surface px-3 py-2 text-sm text-ink outline-none transition-colors placeholder:text-subtle focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]",
        mono && "font-mono text-[13px]",
        className
      )}
      {...props}
    />
  )
);
Textarea.displayName = "Textarea";